import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { Clock, CreditCard, Calendar, DollarSign, Percent, FileText, Building2, Tag, Users, Calculator } from "lucide-react";

const planLabels: Record<string, string> = { free: "مجاني", basic: "أساسي", premium: "متقدم", enterprise: "مؤسسي" };
const statusLabels: Record<string, string> = { active: "نشط", expired: "منتهي", suspended: "معلق", cancelled: "ملغي" };
const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  expired: "bg-red-100 text-red-800",
  suspended: "bg-yellow-100 text-yellow-800",
  cancelled: "bg-muted text-muted-foreground",
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  subscription: any | null;
}

const SubscriptionDetailsDialog = ({ open, onOpenChange, subscription: sub }: Props) => {
  if (!sub) return null;

  const isTrial = sub.is_trial;
  const nurseryName = sub.nurseries?.name || "—";
  const isExpired = sub.expires_at && new Date(sub.expires_at) < new Date();
  const daysLeft = sub.expires_at ? Math.ceil((new Date(sub.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

  const InfoRow = ({ icon: Icon, label, value, color }: { icon: any; label: string; value: React.ReactNode; color?: string }) => (
    <div className="flex items-start gap-3 py-2">
      <div className="p-1.5 rounded-lg bg-muted"><Icon className={`w-4 h-4 ${color || "text-muted-foreground"}`} /></div>
      <div className="flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            تفاصيل اشتراك: {nurseryName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1">
          {/* Status Banner */}
          <div className={`rounded-lg p-3 text-center ${isTrial ? "bg-blue-50 dark:bg-blue-950/20" : isExpired ? "bg-red-50 dark:bg-red-950/20" : "bg-green-50 dark:bg-green-950/20"}`}>
            <Badge className={`text-sm px-3 py-1 ${isTrial ? "bg-blue-100 text-blue-800" : isExpired ? "bg-red-100 text-red-800" : statusColors[sub.status]}`}>
              {isTrial ? "🔵 فترة تجريبية" : isExpired ? "🔴 منتهي" : `🟢 ${statusLabels[sub.status] || sub.status}`}
            </Badge>
            {daysLeft !== null && !isExpired && (
              <p className="text-xs text-muted-foreground mt-1">متبقي {daysLeft} يوم</p>
            )}
          </div>

          <Separator />

          <InfoRow icon={Tag} label="الخطة" value={<Badge variant="outline">{planLabels[sub.plan] || sub.plan}</Badge>} />
          <InfoRow icon={CreditCard} label="دورة الفوترة" value={sub.billing_cycle === "yearly" ? "سنوي" : "شهري"} />
          <InfoRow icon={Calculator} label="نموذج التسعير" value={
            sub.pricing_model === "per_child" ? (
              <Badge variant="secondary" className="gap-1"><Users className="w-3 h-3" />حسب عدد الأطفال</Badge>
            ) : (
              <Badge variant="outline">ثابت (باقة)</Badge>
            )
          } />

          {sub.pricing_model === "per_child" && (
            <>
              <InfoRow icon={Users} label="سعر الطفل" value={`${(sub.price_per_child || 0).toLocaleString()} ج.م`} color="text-primary" />
              <InfoRow icon={DollarSign} label="الحد الأدنى" value={`${(sub.min_charge || 0).toLocaleString()} ج.م`} />
            </>
          )}

          <InfoRow icon={DollarSign} label="المبلغ" value={isTrial ? "مجاني" : `${(sub.amount || 0).toLocaleString()} ج.م`} color="text-primary" />
          
          {sub.original_amount > 0 && sub.original_amount !== sub.amount && (
            <InfoRow icon={DollarSign} label="المبلغ الأصلي" value={<span className="line-through text-muted-foreground">{sub.original_amount.toLocaleString()} ج.م</span>} />
          )}
          
          {(sub.discount_percent || 0) > 0 && (
            <InfoRow icon={Percent} label="الخصم" value={
              <span className="text-green-600">{sub.discount_percent}% ({sub.discount_type === "annual" ? "سنوي" : sub.discount_type === "group" ? "مجموعة" : sub.discount_type === "promo" ? "كود ترويجي" : sub.discount_type})</span>
            } color="text-green-600" />
          )}

          <Separator />

          <InfoRow icon={Calendar} label="تاريخ البدء" value={format(new Date(sub.started_at), "dd/MM/yyyy")} />
          <InfoRow icon={Calendar} label="تاريخ الانتهاء" value={
            sub.expires_at ? (
              <span className={isExpired ? "text-destructive font-bold" : ""}>
                {format(new Date(sub.expires_at), "dd/MM/yyyy")}
                {isExpired && " (منتهي)"}
              </span>
            ) : "—"
          } />

          {isTrial && sub.trial_ends_at && (
            <InfoRow icon={Clock} label="انتهاء التجربة" value={format(new Date(sub.trial_ends_at), "dd/MM/yyyy")} color="text-blue-600" />
          )}

          {sub.payment_method && (
            <InfoRow icon={CreditCard} label="طريقة الدفع" value={
              sub.payment_method === "bank_transfer" ? "تحويل بنكي" :
              sub.payment_method === "cash" ? "نقدي" :
              sub.payment_method === "online" ? "إلكتروني" :
              sub.payment_method === "card" ? "بطاقة" :
              sub.payment_method === "wallet" ? "محفظة إلكترونية" : sub.payment_method
            } />
          )}

          {sub.notes && (
            <>
              <Separator />
              <InfoRow icon={FileText} label="ملاحظات" value={sub.notes} />
            </>
          )}

          <Separator />
          <div className="flex justify-between text-[10px] text-muted-foreground pt-1">
            <span>إنشاء: {format(new Date(sub.created_at), "dd/MM/yyyy HH:mm")}</span>
            <span>آخر تحديث: {format(new Date(sub.updated_at), "dd/MM/yyyy HH:mm")}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionDetailsDialog;
